// src/pages/TenantPortal.jsx

import { useEffect, useMemo, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import {
  fetchTenantProperty,
  fetchRentStatus,
  fetchTenantTasks,
  fetchManagerContact,
  fetchTenantDocuments,
  submitMaintenanceRequest,
  fetchTenantProfile,
  fetchTenantJobs,
  respondToJobSchedule,
} from "../api/tenantPortal";

import { AuthContext } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import { createIdempotencyKey } from "../lib/idempotency";
import { formatDate, formatDateTime, formatStatus } from "../lib/formatters";
import { useSubmissionLock } from "../hooks/useSubmissionLock";

function normalizeTenantJob(job) {
  if (!job) return null;

  return {
    ...job,
    id: job.id,
    title: job.title || "Maintenance request",
    description: job.description || "",
    jobType: job.jobType || "MAINTENANCE",
    status: job.status || "NEW",
    priority: job.priority || "MEDIUM",
    createdAt: job.createdAt || job.date || new Date().toISOString(),
    scheduledDate: job.scheduledDate || null,
    schedule: job.schedule || {
      status: job.scheduledDate ? "CONFIRMED" : "NOT_STARTED",
      scheduledStartAt: job.scheduledDate || null,
    },
    assignedContractor: job.assignedContractor || job.assignedWorker || null,
  };
}

function normalizeTenantJobs(value) {
  const jobsArray = value?.data ?? value ?? [];
  if (!Array.isArray(jobsArray)) return [];
  return jobsArray.map(normalizeTenantJob).filter(Boolean);
}

export default function TenantPortal() {
  const { user } = useContext(AuthContext);
  const location = useLocation();

  const [greeting, setGreeting] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [profile, setProfile] = useState(null);
  const [property, setProperty] = useState(null);
  const [rentStatus, setRentStatus] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [landlord, setLandlord] = useState(null);

  const [docs, setDocs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [issueTitle, setIssueTitle] = useState("");
  const [issue, setIssue] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [roomLocation, setRoomLocation] = useState("");
  const [availability, setAvailability] = useState("");
  const [permissionToContactTenant, setPermissionToContactTenant] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [scheduleResponseNotes, setScheduleResponseNotes] = useState({});
  const { run: runSubmission, isPending } = useSubmissionLock();
  const targetHash = useMemo(() => {
    const raw = String(location.hash || "").replace(/^#/, "");
    if (!raw) return "";
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }, [location.hash]);

  useEffect(() => {
    const hour = new Date().getHours();

    setGreeting(
      hour < 12
        ? "Good morning"
        : hour < 18
        ? "Good afternoon"
        : "Good evening"
    );
  }, []);

  useEffect(() => {
    if (!targetHash || loading || jobs.length === 0) return undefined;

    const timer = window.setTimeout(() => {
      const target = document.getElementById(targetHash);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof target.focus === "function") target.focus({ preventScroll: true });
    }, 150);

    return () => window.clearTimeout(timer);
  }, [jobs.length, loading, targetHash]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);

      try {
        const mePromise = fetchTenantProfile().catch(() => null);

        const [
          meResp,
          propResp,
          rentResp,
          tasksResp,
          jobsResp,
          landlordResp,
          docsResp,
        ] = await Promise.all([
          mePromise,
          fetchTenantProperty(),
          fetchRentStatus(),
          fetchTenantTasks(),
          fetchTenantJobs(),
          fetchManagerContact(),
          fetchTenantDocuments(),
        ]);

        setProfile(meResp || null);

        setProperty(propResp?.data ?? propResp ?? null);
        setRentStatus(rentResp?.data ?? rentResp ?? null);

        const tasksArray = tasksResp?.data ?? tasksResp ?? [];
        setTasks(Array.isArray(tasksArray) ? tasksArray : []);
        setJobs(normalizeTenantJobs(jobsResp));

        setLandlord(landlordResp?.data ?? landlordResp ?? null);

        const docsArray = docsResp?.data ?? docsResp ?? [];

        setDocs(Array.isArray(docsArray) ? docsArray : []);
      } catch (err) {
        console.error("[TenantPortal] fetch error:", err);

        setError({
          message:
            err?.body?.error ||
            err?.message ||
            "Failed to load tenant data.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const handleSubmit = async () => {
    if (submittingRequest || isPending("tenant-maintenance-request")) return;
    if (!issue.trim()) {
      alert("Please describe the issue before submitting.");
      return;
    }

    await runSubmission("tenant-maintenance-request", async () => {
      try {
        setSubmittingRequest(true);
        const idempotencyKey = createIdempotencyKey("tenant-maintenance-request");
        const created = await submitMaintenanceRequest({
          title: issueTitle.trim() || "Maintenance request",
          description: issue.trim(),
          priority,
          roomLocation: roomLocation.trim(),
          availability: availability.trim(),
          permissionToContactTenant,
          idempotencyKey,
        }, { idempotencyKey });
        const createdJob = normalizeTenantJob(created?.data ?? created);

        if (createdJob?.id) {
          setJobs((prev) => [
            createdJob,
            ...prev.filter((job) => job.id !== createdJob.id),
          ]);
        }

        try {
          const refreshed = await fetchTenantJobs();
          const refreshedJobs = normalizeTenantJobs(refreshed);

          setJobs((prev) =>
            refreshedJobs.length > 0
              ? refreshedJobs
              : createdJob?.id
              ? [
                  createdJob,
                  ...prev.filter((job) => job.id !== createdJob.id),
                ]
              : prev
          );
        } catch (e) {
          console.warn(
            "[TenantPortal] jobs refresh failed:",
            e
          );
        }

        setIssueTitle("");
        setIssue("");
        setPriority("MEDIUM");
        setRoomLocation("");
        setAvailability("");
        setPermissionToContactTenant(false);

        alert("Maintenance request submitted!");
      } catch (err) {
        console.error(
          "[TenantPortal] submit request failed:",
          err
        );

        alert(
          err?.body?.error ||
            err?.message ||
            "Failed to submit request."
        );
      } finally {
        setSubmittingRequest(false);
      }
    });
  };

  const handleScheduleResponse = async (job, action) => {
    await runSubmission(`tenant-schedule:${job.id}`, async () => {
      const notes = action === "request_reschedule"
        ? (scheduleResponseNotes[job.id] || "").trim()
        : "";

      try {
        const updated = await respondToJobSchedule(job.id, {
          action,
          notes,
          idempotencyKey: createIdempotencyKey(`tenant-schedule-${action}`),
        });
        const updatedJob = normalizeTenantJob(updated?.data ?? updated);
        if (updatedJob?.id) {
          setJobs((prev) =>
            prev.map((item) => (item.id === updatedJob.id ? updatedJob : item))
          );
        }
      } catch (err) {
        console.error("[TenantPortal] schedule response failed:", err);
        alert(
          err?.body?.error ||
            err?.message ||
            "Failed to update appointment response."
        );
      }
    });
  };

  function openAssignedDocument(doc) {
    if (!doc?.id) return;

    const apiOrigin =
      (axiosInstance?.defaults?.baseURL &&
        String(
          axiosInstance.defaults.baseURL
        ).replace(/\/$/, "")) ||
      "";

    const openUrl = apiOrigin
      ? `${apiOrigin}/api/tenant-portal/documents/${encodeURIComponent(
          doc.id
        )}/open`
      : `/api/tenant-portal/documents/${encodeURIComponent(
          doc.id
        )}/open`;

    window.open(openUrl, "_blank", "noopener");
  }

  if (loading) {
    return (
      <div className="min-h-screen p-6 text-white font-sans">
        Loading tenant portal…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-6 text-white font-sans text-red-400">
        Error: {error.message}
      </div>
    );
  }

  const fullName =
    (profile?.firstName &&
      profile.firstName.trim()) ||
    (user?.firstName &&
      user.firstName.trim()) ||
    (profile?.name && profile.name.trim()) ||
    (user?.name && user.name.trim()) ||
    "";

  const firstName = fullName
    ? fullName.split(" ")[0]
    : "";

  const paymentDetails = rentStatus?.paymentDetails || {};
  const hasPaymentDetails = Boolean(paymentDetails.isConfigured);
  const bankRows = [
    ["Account Name", paymentDetails.accountName],
    ["Bank Name", paymentDetails.bankName],
    ["Account Number", paymentDetails.accountNumber],
    ["Sort Code", paymentDetails.sortCode],
    ["Reference", paymentDetails.reference],
  ];

  return (
    <div className="min-h-screen p-6 text-white font-sans">
      {/* Header */}
      <header className="flex justify-between items-center mb-6">
        <h1 className="text-4xl">
          {greeting}
          {firstName ? `, ${firstName}` : ""}
        </h1>

        <button
          className="px-4 py-2 bg-[#AF9B8A]/90 rounded-md hover:bg-[#AF9B8A] transition"
          onClick={() => (window.location.href = "/")}
        >
          Log Out
        </button>
      </header>

      <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6" />

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* Rent Payment */}
        <div className="bg-white/10 p-6 rounded-xl border border-white/10 backdrop-blur-md">
          <h2 className="text-xl font-semibold mb-4">
            Rent Payment
          </h2>

          <p className="text-sm text-gray-300 mb-4">
            Next payment due:{" "}
            <strong>
              {rentStatus?.nextPaymentDate
                ? formatDate(rentStatus.nextPaymentDate)
                : "TBA"}
            </strong>
          </p>

          <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
            <h3 className="text-sm font-semibold text-[#AF9B8A] mb-3">
              Bank Account Details
            </h3>

            {hasPaymentDetails ? (
              <div className="space-y-3 text-sm">
                {bankRows.map(([label, value]) => (
                  <div key={label}>
                    <p className="text-gray-400">
                      {label}
                    </p>

                    <p
                      className={`font-medium ${
                        label === "Reference"
                          ? "text-[#AF9B8A]"
                          : "text-white tracking-wider"
                      }`}
                    >
                      {value || "—"}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">
                The landlord has not added bank details yet.
              </p>
            )}
          </div>

          <div className="mt-4 text-sm text-gray-400 space-y-2">
            <p>
              Last payment:{" "}
              <strong>
                {rentStatus?.lastPaymentAmount
                  ? `£${rentStatus.lastPaymentAmount}`
                  : "—"}
              </strong>{" "}
              on{" "}
              <strong>
                {rentStatus?.lastPaymentDate
                  ? formatDate(rentStatus.lastPaymentDate)
                  : "—"}
              </strong>
            </p>

            <p>
              Outstanding:{" "}
              <strong>
                {rentStatus?.outstandingBalance !=
                null
                  ? `£${rentStatus.outstandingBalance}`
                : "—"}
              </strong>
            </p>
          </div>
        </div>

        {/* Maintenance */}
        <div className="bg-white/10 p-6 rounded-xl border border-white/10 backdrop-blur-md">
          <h2 className="text-xl font-semibold mb-2">
            Maintenance Request
          </h2>

          <input
            className="w-full p-3 rounded-lg bg-white/5 text-sm text-white border border-white/10 mb-3 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
            placeholder="Issue title"
            value={issueTitle}
            onChange={(e) => setIssueTitle(e.target.value)}
          />

          <textarea
            className="w-full p-3 rounded-lg bg-white/5 text-sm text-white border border-white/10 mb-3 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
            rows="4"
            placeholder="Describe the issue..."
            value={issue}
            onChange={(e) =>
              setIssue(e.target.value)
            }
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <select
              className="w-full p-3 rounded-lg bg-white/5 text-sm text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              <option className="text-black" value="LOW">Low</option>
              <option className="text-black" value="MEDIUM">Medium</option>
              <option className="text-black" value="HIGH">High</option>
              <option className="text-black" value="EMERGENCY">Emergency</option>
            </select>

            <input
              className="w-full p-3 rounded-lg bg-white/5 text-sm text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              placeholder="Room / location"
              value={roomLocation}
              onChange={(e) => setRoomLocation(e.target.value)}
            />
          </div>

          <textarea
            className="w-full p-3 rounded-lg bg-white/5 text-sm text-white border border-white/10 mb-3 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
            rows="2"
            placeholder="Availability or access notes"
            value={availability}
            onChange={(e) => setAvailability(e.target.value)}
          />

          <label className="flex items-center gap-2 text-sm text-gray-300 mb-4">
            <input
              type="checkbox"
              checked={permissionToContactTenant}
              onChange={(e) => setPermissionToContactTenant(e.target.checked)}
              className="appearance-none w-5 h-5 border-2 border-[#AF9B8A] rounded-md checked:bg-[#AF9B8A] checked:border-[#AF9B8A]"
            />
            Contractor may contact me about this job
          </label>

          <button
            className="bg-[#AF9B8A]/90 px-4 py-2 rounded-md text-sm hover:bg-[#AF9B8A] transition disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSubmit}
            disabled={submittingRequest}
          >
            {submittingRequest ? "Submitting..." : "Submit Request"}
          </button>
        </div>

        {/* Jobs */}
        <div className="bg-white/10 p-6 rounded-xl border border-white/10 backdrop-blur-md">
          <h2 className="text-xl font-semibold mb-2">
            Jobs
          </h2>

          {jobs.length === 0 ? (
            <p className="text-sm text-gray-400">
              No maintenance jobs at the moment.
            </p>
          ) : (
            <ul className="text-sm text-gray-300 space-y-3">
              {jobs.map((job) => (
                <li
                  id={`job-${job.id}`}
                  key={job.id}
                  tabIndex={-1}
                  className="border-b border-white/10 pb-3 last:border-b-0"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium text-white">{job.title}</span>
                    <span className="text-xs px-2 py-1 rounded-md bg-white/10 text-white/70">
                      {formatStatus(job.status)}
                    </span>
                  </div>
                  <p className="mt-1 text-gray-400">{job.description}</p>
                  <p className="mt-2 text-gray-400">
                    Contractor: {job.assignedContractor?.name || 'Not assigned yet'}
                  </p>
                  <p id={`schedule-${job.id}`} className="text-gray-400">
                    Visit: {formatDateTime(job.schedule?.scheduledStartAt || job.scheduledDate, 'Not scheduled yet')}
                  </p>
                  <p className="text-gray-400">
                    Appointment: {formatStatus(job.schedule?.status || 'NOT_STARTED')}
                  </p>
                  {job.schedule?.tenantResponse && (
                    <p className="text-gray-400">
                      Your response: {formatStatus(job.schedule.tenantResponse)}
                    </p>
                  )}
                  {job.accessRequirement === 'TENANT_REQUIRED' &&
                    job.status === 'AWAITING_TENANT_CONFIRMATION' &&
                    job.schedule?.status === 'PROPOSED' && (
                    <div className="mt-3 space-y-3">
                      <textarea
                        value={scheduleResponseNotes[job.id] || ""}
                        onChange={(e) =>
                          setScheduleResponseNotes((current) => ({
                            ...current,
                            [job.id]: e.target.value,
                          }))
                        }
                        rows="2"
                        placeholder="If this time does not work, suggest another time"
                        className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                      />
                      <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleScheduleResponse(job, 'confirm')}
                        disabled={isPending(`tenant-schedule:${job.id}`)}
                        className="px-3 py-2 rounded-md bg-[#AF9B8A]/90 text-white hover:bg-[#AF9B8A] transition disabled:opacity-50"
                        aria-busy={isPending(`tenant-schedule:${job.id}`)}
                      >
                        {isPending(`tenant-schedule:${job.id}`) ? "Confirming..." : "This works"}
                      </button>
                      <button
                        onClick={() => handleScheduleResponse(job, 'request_reschedule')}
                        disabled={isPending(`tenant-schedule:${job.id}`)}
                        className="px-3 py-2 rounded-md bg-white/10 border border-white/10 text-white disabled:opacity-50"
                        aria-busy={isPending(`tenant-schedule:${job.id}`)}
                      >
                        {isPending(`tenant-schedule:${job.id}`) ? "Submitting..." : "Request another time"}
                      </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Documents */}
        <div className="bg-white/10 p-6 rounded-xl border border-white/10 backdrop-blur-md">
          <h2 className="text-xl font-semibold mb-2">
            Documents
          </h2>

          {docs.length === 0 ? (
            <div className="text-sm text-gray-400">
              No documents have been assigned
              to you yet.
            </div>
          ) : (
            <ul className="text-sm text-gray-300 space-y-2">
              {docs.map((d) => (
                <li key={d.id}>
                  <button
                    className="underline hover:text-white"
                    onClick={() =>
                      openAssignedDocument(d)
                    }
                  >
                    {d.title || "Document"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Lease Summary */}
        <div className="bg-white/10 p-6 rounded-xl border border-white/10 backdrop-blur-md">
          <h2 className="text-xl font-semibold mb-2">
            Lease Summary
          </h2>

          <p className="text-sm text-gray-300">
            Property:{" "}
            <strong>
              {property?.title ?? "—"}
            </strong>
          </p>

          <p className="text-sm text-gray-300">
            Start Date:{" "}
            <strong>
              {(() => {
                const d =
                  rentStatus?.tenancy
                    ?.startDate ||
                  rentStatus?.startDate ||
                  property?.leaseStartDate ||
                  null;

                return formatDate(d, "—");
              })()}
            </strong>
          </p>

          <p className="text-sm text-gray-300">
            End Date:{" "}
            <strong>
              {(() => {
                const d =
                  rentStatus?.tenancy?.endDate ||
                  rentStatus?.endDate ||
                  property?.leaseEndDate ||
                  null;

                return formatDate(d, "—");
              })()}
            </strong>
          </p>
        </div>

        {/* Landlord Contact */}
        <div className="bg-white/10 p-6 rounded-xl border border-white/10 backdrop-blur-md">
          <h2 className="text-xl font-semibold mb-2">
            Landlord Contact
          </h2>

          {landlord &&
          !landlord.notConfigured ? (
            <div className="text-sm text-gray-300 space-y-2">
              <p>
                Name:{" "}
                <strong>
                  {landlord?.name ?? "N/A"}
                </strong>
              </p>

              <p>
                Email:{" "}
                <strong>
                  {landlord?.email ?? "N/A"}
                </strong>
              </p>

              <p>
                Phone:{" "}
                <strong>
                  {landlord?.phone ?? "N/A"}
                </strong>
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-400">
              Manager contact not configured.
            </p>
          )}
        </div>

        {/* Tasks */}
        <div className="bg-white/10 p-6 rounded-xl border border-white/10 backdrop-blur-md">
          <h2 className="text-xl font-semibold mb-2">
            Tasks / To-Dos
          </h2>

          <ul className="text-sm text-gray-300 space-y-1">
            {tasks.length ? (
              tasks.map((task) => (
                <li key={task.id}>
                  {task.title}
                </li>
              ))
            ) : (
              <li>
                No tasks at the moment.
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
